import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Clock, Calendar, Plus, Check, X, AlertCircle, 
  User, Filter, RefreshCw, ArrowLeft, Trash2, Edit
} from 'lucide-react';
import { toast } from 'sonner';
import { useCompanyScope } from '../context/CompanyScopeContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const REASON_LABELS = {
  health: 'Raison de santé',
  personal_request: 'Demande personnelle',
  office_errand: 'Course de bureau',
  other: 'Autre'
};

const STATUS_LABELS = {
  pending: 'En attente',
  approved: 'Approuvé',
  rejected: 'Refusé',
  active: 'Actif',
  expired: 'Expiré',
  cancelled: 'Annulé'
};

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-800',
  rejected: 'bg-red-100 text-red-800',
  active: 'bg-green-100 text-green-800',
  expired: 'bg-slate-100 text-slate-600',
  cancelled: 'bg-slate-100 text-slate-600'
};

export default function ScheduleExceptions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { selectedCompanyId, isCrossCompany } = useCompanyScope();
  const [exceptions, setExceptions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [filterEmployee, setFilterEmployee] = useState('all');
  
  const [formData, setFormData] = useState({
    employee_id: '',
    start_date: '',
    end_date: '',
    adjusted_start_time: '10:00',
    adjusted_end_time: '19:00',
    reason: 'personal_request',
    reason_details: ''
  });

  const canManage = ['super_admin', 'company_admin', 'hr', 'director'].includes(user?.role);

  useEffect(() => {
    fetchExceptions();
    if (canManage) {
      fetchEmployees();
    }
  }, [activeTab]);

  const fetchExceptions = async () => {
    try {
      setLoading(true);
      const includeExpired = activeTab === 'history';
      const status = activeTab === 'active' ? 'active' : (activeTab === 'pending' ? 'pending' : null);
      
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (includeExpired) params.append('include_expired', 'true');
      if (filterEmployee && filterEmployee !== 'all') params.append('employee_id', filterEmployee);
      
      const response = await api.get(`/schedule-exceptions?${params.toString()}`);
      setExceptions(response.data);
    } catch (error) {
      console.error('Failed to fetch schedule exceptions:', error);
      toast.error('Erreur lors du chargement des exceptions');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data.filter(e => e.status === 'active'));
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.employee_id) {
      toast.error('Veuillez sélectionner un employé');
      return;
    }
    
    try {
      const response = await api.post(`/employees/${formData.employee_id}/schedule-exception`, {
        start_date: formData.start_date,
        end_date: formData.end_date,
        adjusted_start_time: formData.adjusted_start_time,
        adjusted_end_time: formData.adjusted_end_time,
        reason: formData.reason,
        reason_details: formData.reason_details || null
      });
      
      toast.success(response.data.message);
      setDialogOpen(false);
      resetForm();
      fetchExceptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleApprove = async (exceptionId) => {
    try {
      await api.post(`/schedule-exceptions/${exceptionId}/approve`);
      toast.success('Exception approuvee');
      fetchExceptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleReject = async (exceptionId) => {
    try {
      await api.post(`/schedule-exceptions/${exceptionId}/reject`);
      toast.success('Exception refusee');
      fetchExceptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleCancel = async (exceptionId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette exception d\'horaire ?')) {
      return;
    }
    
    try {
      await api.delete(`/schedule-exceptions/${exceptionId}`);
      toast.success('Exception annulée');
      fetchExceptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      start_date: '',
      end_date: '',
      adjusted_start_time: '10:00',
      adjusted_end_time: '19:00',
      reason: 'personal_request',
      reason_details: ''
    });
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'dd MMM yyyy', { locale: fr });
    } catch {
      return dateStr;
    }
  };

  const scopedEmployees = useMemo(() => {
    if (!isCrossCompany || !selectedCompanyId) return employees;
    return employees.filter(e => e.company_id === selectedCompanyId);
  }, [employees, selectedCompanyId, isCrossCompany]);

  const getFilteredExceptions = () => {
    // Filtre d'abord par entreprise
    let filtered = (isCrossCompany && selectedCompanyId)
      ? exceptions.filter(e => e.company_id === selectedCompanyId)
      : exceptions;

    if (activeTab === 'active') {
      filtered = filtered.filter(e => ['active', 'approved'].includes(e.status));
    } else if (activeTab === 'pending') {
      filtered = filtered.filter(e => e.status === 'pending');
    }

    return filtered;
  };

  return (
    <div className="p-6 space-y-6" data-testid="schedule-exceptions-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Clock className="w-6 h-6" />
            Exceptions d'Horaire
          </h1>
          <p className="text-slate-600 mt-1">
            Gérez les ajustements temporaires d'horaires pour les employés
          </p>
        </div>
        
        {canManage && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-exception-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle Exception
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Créer une Exception d'Horaire</DialogTitle>
                <DialogDescription>
                  Permet à un employé de travailler sur des horaires différents temporairement
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Employee Selection */}
                <div className="space-y-2">
                  <Label htmlFor="employee_id">Employé *</Label>
                  <Select
                    value={formData.employee_id}
                    onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
                  >
                    <SelectTrigger data-testid="employee-select">
                      <SelectValue placeholder="Sélectionner un employé" />
                    </SelectTrigger>
                    <SelectContent>
                     {scopedEmployees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.first_name} {emp.last_name} ({emp.matricule})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Date début *</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                      data-testid="start-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_date">Date fin *</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      min={formData.start_date}
                      required
                      data-testid="end-date-input"
                    />
                  </div>
                </div>

                {/* Adjusted Hours */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="adjusted_start_time">Heure début ajustée *</Label>
                    <Input
                      id="adjusted_start_time"
                      type="time"
                      value={formData.adjusted_start_time}
                      onChange={(e) => setFormData({ ...formData, adjusted_start_time: e.target.value })}
                      required
                      data-testid="adjusted-start-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="adjusted_end_time">Heure fin ajustée *</Label>
                    <Input
                      id="adjusted_end_time"
                      type="time"
                      value={formData.adjusted_end_time}
                      onChange={(e) => setFormData({ ...formData, adjusted_end_time: e.target.value })}
                      required
                      data-testid="adjusted-end-input"
                    />
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <AlertCircle className="w-4 h-4 inline mr-1" />
                    L'employé pourra arriver à {formData.adjusted_start_time || '--:--'} au lieu de l'heure normale, 
                    mais le calcul de salaire restera basé sur 8h de travail.
                  </p>
                </div>

                {/* Reason */}
                <div className="space-y-2">
                  <Label htmlFor="reason">Raison *</Label>
                  <Select
                    value={formData.reason}
                    onValueChange={(value) => setFormData({ ...formData, reason: value })}
                  >
                    <SelectTrigger data-testid="reason-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="health">Raison de santé</SelectItem>
                      <SelectItem value="personal_request">Demande personnelle</SelectItem>
                      <SelectItem value="office_errand">Course de bureau</SelectItem>
                      <SelectItem value="other">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Reason Details */}
                <div className="space-y-2">
                  <Label htmlFor="reason_details">Détails (optionnel)</Label>
                  <Textarea
                    id="reason_details"
                    value={formData.reason_details}
                    onChange={(e) => setFormData({ ...formData, reason_details: e.target.value })}
                    placeholder="Précisions sur la raison de l'exception..."
                    rows={2}
                    data-testid="reason-details-input"
                  />
                </div>

                {/* Approval Notice */}
                {user?.role !== 'director' && user?.role !== 'super_admin' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-800">
                      <AlertCircle className="w-4 h-4 inline mr-1" />
                      Cette demande nécessitera l'approbation du directeur avant d'être activée.
                    </p>
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                    Annuler
                  </Button>
                  <Button type="submit" className="flex-1" data-testid="submit-exception-btn">
                    Créer l'Exception
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Tabs & Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-auto">
              <TabsList>
                <TabsTrigger value="active" data-testid="tab-active">
                  Actives
                </TabsTrigger>
                <TabsTrigger value="pending" data-testid="tab-pending">
                  En attente
                </TabsTrigger>
                <TabsTrigger value="history" data-testid="tab-history">
                  Historique
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="flex gap-2 items-center">
              <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filtrer par employé" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les employés</SelectItem>
                  {scopedEmployees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button variant="outline" size="icon" onClick={fetchExceptions}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Exceptions List */}
      <Card>
        <CardHeader>
          <CardTitle>
            {activeTab === 'active' && 'Exceptions Actives'}
            {activeTab === 'pending' && 'Exceptions en Attente'}
            {activeTab === 'history' && 'Historique des Exceptions'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Chargement...</div>
          ) : getFilteredExceptions().length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              Aucune exception d'horaire {activeTab === 'pending' ? 'en attente' : 'trouvée'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employé</TableHead>
                  <TableHead>Période</TableHead>
                  <TableHead>Horaires Ajustés</TableHead>
                  <TableHead>Raison</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Demandeur</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getFilteredExceptions().map((exception) => (
                  <TableRow key={exception.id} data-testid={`exception-row-${exception.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        {exception.employee_name || 'N/A'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        {formatDate(exception.start_date)} - {formatDate(exception.end_date)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4 text-slate-400" />
                        {exception.adjusted_start_time} - {exception.adjusted_end_time}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {REASON_LABELS[exception.reason] || exception.reason}
                      </span>
                      {exception.reason_details && (
                        <p className="text-xs text-slate-500 truncate max-w-[150px]" title={exception.reason_details}>
                          {exception.reason_details}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[exception.status]}`}>
                        {STATUS_LABELS[exception.status] || exception.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {exception.requested_by_name || 'N/A'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        {exception.status === 'pending' && canManage && (
                          <>
                            <Button
                              size="sm"
                              variant="default"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => handleApprove(exception.id)}
                              data-testid={`approve-exception-${exception.id}`}
                            >
                              <Check className="w-4 h-4 mr-1" /> Valider
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleReject(exception.id)}
                              data-testid={`reject-exception-${exception.id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {['approved', 'active'].includes(exception.status) && canManage && (
                          <Badge className="bg-green-100 text-green-700 mr-2" variant="outline">Validee</Badge>
                        )}
                        {['pending', 'approved', 'active'].includes(exception.status) && canManage && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleCancel(exception.id)}
                            data-testid={`cancel-exception-${exception.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
